import type { CSSProperties } from 'react';
import styled from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import useProgressiveImg from 'hooks/use-progressive-image';
import type { ContentImage } from 'lib/pdp-modules/types';

const ImagesWrapper = styled.div`
  --small-image: 8.875rem;

  max-height: 27.5rem;
  max-width: 38.5625rem;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr var(--small-image);
  grid-template-rows: repeat(2, auto);
  gap: 1rem;
  overflow: hidden;

  @media ${mqLargeMin} {
    --small-image: 13.25rem;
  }
`;

const ImageWrapper = styled.div`
  overflow: hidden;
`;

const BenefitImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProductBenefitImage = ({
  image,
  extraStyles,
}: {
  image: ContentImage;
  extraStyles?: CSSProperties;
}) => {
  const currentImage = useProgressiveImg({
    mediaItem: image,
    mediaSize: 'medium',
  });

  return (
    <BenefitImage
      ref={currentImage.ref}
      src={currentImage.src}
      alt={image.alt}
      style={{ ...extraStyles, ...currentImage.blurStyles }}
    />
  );
};

const ProductBenefitImages = ({ images }: { images?: ContentImage[] }) => {
  const getRowAndCol = (index) => {
    let row = '1';
    let col = `${index + 1}`;
    let borderRadius = '2rem';
    let maxHeight = '100%';

    if (images?.length === 1) {
      // display image in both columns
      col = '1 / span 2';
    } else if (images?.length === 3) {
      // ensure first image is in both rows
      if (index === 0) {
        row = '1 / span 2';
      }
      // ensure last image is in bottom right
      if (index === 2) {
        row = '2';
        col = '2';
      }

      // set our border radius
      if (index === 1) {
        borderRadius = '100%';
      } else if (index === 2) {
        borderRadius = '1rem';
      }

      // set our max height
      if (index > 0) {
        maxHeight = 'var(--small-image)';
      }
    }

    return {
      gridRow: row,
      gridColumn: col,
      height: maxHeight,
      borderRadius: borderRadius,
    };
  };

  return (
    <ImagesWrapper>
      {images?.map((image, imageIndex) => (
        <ImageWrapper
          key={`benefit-image-${image.mediumUrl}`}
          style={getRowAndCol(imageIndex)}
        >
          <ProductBenefitImage image={image} />
        </ImageWrapper>
      ))}
    </ImagesWrapper>
  );
};

export default ProductBenefitImages;
